// ============================================================================
// WORK ITEM MODAL
// Type-specific create/edit modal with field configurations
// Date: October 13, 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { WorkItem, WorkItemType } from './UniversalWorkItems';
import ContactPicker from './ContactPicker';
import LocationPicker from './LocationPicker';
import UserMultiSelect from './UserMultiSelect';
import DynamicFieldInput from './DynamicFieldInput';
import { isCapacitor } from '@/lib/platformUtils';
import { useOperationsAssignmentOptions } from '@/hooks/useOperationsAssignmentOptions';

const MOBILE_SAFE_AREA_TOP = 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))';
const MOBILE_SAFE_AREA_BOTTOM = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-bottom, 0px))';
const EMPTY_TEMPLATES: WorkItemTemplate[] = [];

// Template interfaces
export interface WorkItemTemplate {
  id: number;
  client_id: number | null;
  template_key: string;
  template_label: string; // API returns template_label from DB
  template_name?: string; // Compatibility alias
  applies_to_item_type?: string | null;
  applies_to_subtype: string; // Changed from item_subtype to match DB
  item_subtype?: string; // Compatibility alias
  description: string | null;
  icon: string | null;
  color: string | null;
  enabled?: boolean; // Changed from is_active to match DB
  fields?: { fields: TemplateField[] }; // JSONB field from DB (field definitions)
  field_definitions?: TemplateField[]; // Compatibility alias
  sort_order?: number;
  is_system?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number | null;
}

export interface TemplateField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'datetime' | 'select' | 'multi-select' | 'boolean' | 'textarea';
  required?: boolean;
  placeholder?: string;
  options?: string[] | Array<{ value: string; label: string }>; // Support both formats
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
  };
  prefix?: string;
  suffix?: string;
  section?: string;
  min?: number;
  max?: number;
}

// ============================================================================
// TYPE CONFIGURATIONS
// ============================================================================

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'select' | 'number' | 'tags' | 'contact_picker' | 'location_picker' | 'user_multi_select';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  visible: (type: WorkItemType) => boolean;
  moduleKey?: string; // For user_multi_select: filter users by module access
}

const FIELD_CONFIGS: FieldConfig[] = [
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: true,
    placeholder: 'Enter a descriptive title',
    visible: () => true
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Provide details...',
    visible: () => true
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    options: [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'blocked', label: 'Blocked' },
      { value: 'done', label: 'Done' },
      { value: 'closed', label: 'Closed' }
    ],
    visible: () => true
  },
  {
    name: 'priority',
    label: 'Priority',
    type: 'select',
    required: true,
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
      { value: 'critical', label: 'Critical' }
    ],
    visible: () => true
  },
  {
    name: 'due_date',
    label: 'Due Date',
    type: 'date',
    visible: (type) => ['ticket', 'task', 'milestone'].includes(type)
  },
  {
    name: 'start_date',
    label: 'Start Date',
    type: 'date',
    visible: (type) => ['project', 'sprint', 'job'].includes(type)
  },
  {
    name: 'end_date',
    label: 'End Date',
    type: 'date',
    visible: (type) => ['project', 'sprint', 'job'].includes(type)
  },
  {
    name: 'estimated_hours',
    label: 'Estimated Hours',
    type: 'number',
    placeholder: '0',
    visible: (type) => ['ticket', 'task', 'job'].includes(type)
  },
  {
    name: 'actual_hours',
    label: 'Actual Hours',
    type: 'number',
    placeholder: '0',
    visible: (type) => ['ticket', 'task', 'job'].includes(type)
  },
  {
    name: 'estimated_cost',
    label: 'Estimated Cost',
    type: 'number',
    placeholder: '0.00',
    visible: (type) => ['job', 'project'].includes(type)
  },
  {
    name: 'story_points',
    label: 'Story Points',
    type: 'number',
    placeholder: '0',
    visible: (type) => ['bug', 'feature', 'story', 'epic', 'request', 'task'].includes(type)
  },
  {
    name: 'acceptance_criteria',
    label: 'Acceptance Criteria',
    type: 'textarea',
    placeholder: 'Describe the conditions that must be met for this item to be considered complete...',
    visible: (type) => ['bug', 'feature', 'story', 'epic', 'request', 'task'].includes(type)
  },
  {
    name: 'actual_cost',
    label: 'Actual Cost',
    type: 'number',
    placeholder: '0.00',
    visible: (type) => ['job', 'project'].includes(type)
  },
  {
    name: 'contact_id',
    label: 'Contact',
    type: 'contact_picker',
    visible: (type) => ['job', 'ticket', 'project', 'task'].includes(type)
  },
  {
    name: 'location_id',
    label: 'Location',
    type: 'location_picker',
    visible: (type) => ['job', 'ticket'].includes(type)
  },
  {
    name: 'assigned_users',
    label: 'Assign To',
    type: 'user_multi_select',
    visible: () => true,
    moduleKey: 'total-operations' // Filter to users with total-operations access
  },
  {
    name: 'tags',
    label: 'Tags',
    type: 'tags',
    placeholder: 'Enter tags separated by commas',
    visible: () => true
  }
];

// ============================================================================
// MODAL COMPONENT
// ============================================================================

interface WorkItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<WorkItem>) => Promise<void>;
  itemType: WorkItemType;
  existingItem?: WorkItem | null;
  templates?: WorkItemTemplate[];
  defaultSubtype?: string;
  headerColor?: string; // Gradient color for modal header (e.g., 'from-red-500 to-red-600')
  icon?: React.ElementType; // Icon component to display in header
  label?: string; // Custom label for the modal (e.g., "Report Bug")
  description?: string; // Description text to display in header
  slideOverLayout?: boolean; // Render as right-side slide-over on web/desktop
  statusOptionsOverride?: Array<{ value: string; label: string }>;
  priorityOptionsOverride?: Array<{ value: string; label: string }>;
  /** Default status key for new items (e.g., from workspace item_types.default_status). */
  defaultStatus?: string;
  /** Default priority key for new items (e.g., from workspace priorities.is_default). */
  defaultPriority?: string;
  forceVisibleFieldNames?: string[];
  hideFieldNames?: string[];
  assigneeUserIds?: number[]; // Restrict assignee picker to specific user IDs
  activeSolutionKey?: string | null; // Filter users by team membership for this solution
  /** Workspace-level custom field definitions (rendered as extra form fields). */
  workspaceCustomFields?: Array<{
    key: string;
    label: string;
    type: string;
    options?: string[];
    required?: boolean;
  }>;
}

export default function WorkItemModal({
  isOpen,
  onClose,
  onSave,
  itemType,
  existingItem,
  templates = EMPTY_TEMPLATES,
  defaultSubtype,
  headerColor,
  icon: IconComponent,
  label,
  description,
  slideOverLayout = false,
  statusOptionsOverride,
  priorityOptionsOverride,
  defaultStatus,
  defaultPriority,
  forceVisibleFieldNames = [],
  hideFieldNames = [],
  assigneeUserIds,
  activeSolutionKey,
  workspaceCustomFields,
}: WorkItemModalProps) {
  const [formData, setFormData] = useState<Record<string, any>>({
    title: '',
    description: '',
    status: defaultStatus || 'open',
    priority: defaultPriority || 'medium',
    story_points: '',
    acceptance_criteria: '',
    tags: [],
    contact_id: null,
    location_id: null,
    assigned_users: [],
    custom_fields: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<string | undefined>(defaultSubtype);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkItemTemplate | null>(null);
  const isCapacitorApp = isCapacitor();
  const useSlideOverLayout = slideOverLayout && !isCapacitorApp;

  // Get team member user IDs for filtering when activeSolutionKey is provided
  const { getTeamMemberUserIds } = useOperationsAssignmentOptions({ activeSolutionKey });
  const teamMemberUserIds = useMemo(() => {
    if (!activeSolutionKey) {
      return undefined;
    }
    return getTeamMemberUserIds();
  }, [activeSolutionKey, getTeamMemberUserIds]);
  
  // Load template when subtype changes
  useEffect(() => {
    if (selectedSubtype && templates.length > 0) {
      const template = templates.find(t => t.applies_to_subtype === selectedSubtype);
      
      // Normalize template: field_definitions should be empty array if not present
      // NOTE: template.fields.fields is for basic field config (name, required), NOT custom fields
      if (template) {
        const normalizedTemplate = {
          ...template,
          field_definitions: template.field_definitions || [] // Don't use fields.fields - different structure
        };
        setSelectedTemplate(normalizedTemplate);
      } else {
        setSelectedTemplate(null);
      }
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedSubtype, templates]);
  
  // Set initial subtype from existing item or default
  useEffect(() => {
    if (existingItem?.item_subtype) {
      setSelectedSubtype(existingItem.item_subtype);
    } else if (defaultSubtype) {
      setSelectedSubtype(defaultSubtype);
    }
  }, [existingItem, defaultSubtype, isOpen]);
  
  // Load existing item data
  useEffect(() => {
    if (existingItem) {
      // Helper function to format dates from ISO to yyyy-MM-dd
      const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '';
        try {
          // Handle ISO 8601 dates
          if (dateStr.includes('T')) {
            return dateStr.split('T')[0];
          }
          // Handle yyyy-MM-dd
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return dateStr;
          }
          // Try parsing as Date
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
          }
          return '';
        } catch {
          return '';
        }
      };

      setFormData({
        title: existingItem.title || '',
        description: existingItem.description || '',
        status: existingItem.status || 'open',
        priority: existingItem.priority || 'medium',
        story_points: existingItem.custom_fields?.story_points ?? existingItem.story_points ?? '',
        acceptance_criteria: existingItem.custom_fields?.acceptance_criteria ?? '',
        due_date: formatDate(existingItem.due_date),
        start_date: formatDate(existingItem.start_date),
        end_date: formatDate(existingItem.due_date), // Map due_date to end_date
        estimated_hours: existingItem.estimated_hours || '',
        actual_hours: existingItem.actual_hours || '',
        estimated_cost: existingItem.estimated_cost || '',
        actual_cost: existingItem.actual_cost || '',
        contact_id: existingItem.contact_id || null,
        location_id: existingItem.location_id || null,
        assigned_users: [], // Will be loaded separately if needed
        tags: existingItem.tags || [],
        custom_fields: existingItem.custom_fields || {},
      });
    } else {
      // Reset for new item
      setFormData({
        title: '',
        description: '',
        status: defaultStatus || 'open',
        priority: defaultPriority || 'medium',
        story_points: '',
        acceptance_criteria: '',
        tags: [],
        contact_id: null,
        location_id: null,
        assigned_users: [],
        custom_fields: {}
      });
    }
  }, [existingItem, isOpen, defaultStatus, defaultPriority]);

  // Handle field change
  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle custom field change
  const handleCustomFieldChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [key]: value
      }
    }));
  };
  
  // Handle tags input
  const handleTagsChange = (value: string) => {
    const tags = value.split(',').map(t => t.trim()).filter(t => t);
    setFormData(prev => ({ ...prev, tags }));
  };
  
  // Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Validate required fields
      const visibleFields = FIELD_CONFIGS.filter(f => f.visible(itemType));
      const requiredFields = visibleFields.filter(f => f.required);
      
      for (const field of requiredFields) {
        if (!formData[field.name]) {
          throw new Error(`${field.label} is required`);
        }
      }
      
      // Validate custom fields if template exists
      if (selectedTemplate && selectedTemplate.field_definitions) {
        for (const field of selectedTemplate.field_definitions) {
          if (field.required && !formData.custom_fields?.[field.key]) {
            throw new Error(`${field.label} is required`);
          }
        }
      }

      // Validate workspace custom fields
      if (workspaceCustomFields) {
        for (const field of workspaceCustomFields) {
          if (field.required && !formData.custom_fields?.[field.key]) {
            throw new Error(`${field.label} is required`);
          }
        }
      }
      
      // Clean data (remove empty strings)
      const cleanData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      // Route agile planning fields into custom_fields so they remain portable
      // across template-driven work item types and survive API round-trips.
      const customFields = {
        ...(cleanData.custom_fields || {}),
      };

      if (cleanData.story_points !== undefined) {
        const points = typeof cleanData.story_points === 'number'
          ? cleanData.story_points
          : Number(cleanData.story_points);
        if (Number.isFinite(points)) {
          customFields.story_points = points;
        }
      }

      if (typeof cleanData.acceptance_criteria === 'string' && cleanData.acceptance_criteria.trim().length > 0) {
        customFields.acceptance_criteria = cleanData.acceptance_criteria.trim();
      }

      cleanData.custom_fields = customFields;
      delete cleanData.story_points;
      delete cleanData.acceptance_criteria;
      
      // Add item_subtype if selected
      if (selectedSubtype) {
        cleanData.item_subtype = selectedSubtype;
      }
      
      await onSave(cleanData);
      onClose();
      
    } catch (err: any) {
      console.error('Error saving work item:', err);
      setError(err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const forceVisibleSet = new Set(forceVisibleFieldNames);
  const hiddenFieldSet = new Set(hideFieldNames);
  const visibleFields = FIELD_CONFIGS.filter((field) => {
    if (hiddenFieldSet.has(field.name)) return false;
    if (forceVisibleSet.has(field.name)) return true;
    return field.visible(itemType || 'job');
  });

  const modalPanel = (
    <div
      className={`bg-white dark:bg-gray-800 shadow-xl w-full ${useSlideOverLayout ? 'overflow-hidden' : 'overflow-y-auto'} ${
        useSlideOverLayout
          ? 'fixed right-0 z-[121] flex max-w-2xl animate-slide-in-right flex-col rounded-none border-l border-gray-200 dark:border-gray-700'
          : isCapacitorApp
            ? 'max-w-full h-[100dvh] max-h-[100dvh] rounded-none'
            : 'max-w-2xl max-h-[90vh] rounded-lg'
      }`}
      style={useSlideOverLayout
        ? {
            top: MOBILE_SAFE_AREA_TOP,
            bottom: MOBILE_SAFE_AREA_BOTTOM,
            height: `calc(var(--100dvh, 100vh) - ${MOBILE_SAFE_AREA_TOP} - ${MOBILE_SAFE_AREA_BOTTOM})`,
          }
        : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={label || `${existingItem ? 'Edit' : 'Create'} ${itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1) : 'Item'}`}
    >
        {/* Header */}
        <div className={`flex flex-shrink-0 items-center justify-between p-6 border-b ${
          headerColor 
            ? `bg-gradient-to-r ${headerColor} text-white` 
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
        }`} style={isCapacitorApp ? { paddingTop: `calc(${MOBILE_SAFE_AREA_TOP} + 1rem)` } : undefined}>
          <div className="flex items-center gap-3">
            {IconComponent && (
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <IconComponent className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h2 className={`text-xl font-bold ${
                headerColor ? 'text-white' : 'text-gray-900 dark:text-white'
              }`}>
                {label || `${existingItem ? 'Edit' : 'Create'} ${itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1) : 'Item'}`}
              </h2>
              {description && (
                <p className={`text-sm mt-0.5 ${
                  headerColor ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {description}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className={`text-2xl leading-none ${
              headerColor ? 'text-white hover:text-gray-100' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            ×
          </button>
        </div>
        
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className={useSlideOverLayout ? 'flex min-h-0 flex-1 flex-col' : 'p-6 space-y-4'}
        >
          {/* Slide-over mode pins the actions bar — fields scroll inside this
              wrapper; `contents` keeps the centered-modal layout unchanged. */}
          <div className={useSlideOverLayout ? 'min-h-0 flex-1 space-y-4 overflow-y-auto p-6' : 'contents'}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-3 text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
          
          {/* Subtype Selector (only for new items with templates available) */}
          {!existingItem && templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entity Type
              </label>
              <select
                value={selectedSubtype || ''}
                onChange={(e) => setSelectedSubtype(e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Standard {itemType}</option>
                {templates
                  .filter(t => !t.applies_to_item_type || t.applies_to_item_type === itemType)
                  .map(template => (
                    <option key={template.id} value={template.item_subtype || template.applies_to_subtype}>
                      {template.icon} {template.template_label || template.template_name}
                    </option>
                  ))}
              </select>
              {selectedTemplate?.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{selectedTemplate.description}</p>
              )}
            </div>
          )}
          
          {visibleFields.map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {/* Text Input */}
              {field.type === 'text' && (
                <input
                  type="text"
                  value={formData[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={field.required}
                />
              )}
              
              {/* Textarea */}
              {field.type === 'textarea' && (
                <textarea
                  value={formData[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
              
              {/* Select */}
              {field.type === 'select' && (
                <select
                  value={formData[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={field.required}
                >
                  <option value="">Select...</option>
                  {(field.name === 'status' && statusOptionsOverride && statusOptionsOverride.length > 0
                    ? statusOptionsOverride
                    : field.name === 'priority' && priorityOptionsOverride && priorityOptionsOverride.length > 0
                      ? priorityOptionsOverride
                      : field.options || []).map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              
              {/* Date */}
              {field.type === 'date' && (
                <input
                  type="date"
                  value={formData[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
              
              {/* Number */}
              {field.type === 'number' && (
                <input
                  type="number"
                  step={field.name === 'story_points' ? 1 : 0.01}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleChange(field.name, parseFloat(e.target.value) || 0)}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
              
              {/* Tags */}
              {field.type === 'tags' && (
                <div>
                  <input
                    type="text"
                    value={Array.isArray(formData[field.name]) ? formData[field.name].join(', ') : ''}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {formData[field.name] && formData[field.name].length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData[field.name].map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Contact Picker */}
              {field.type === 'contact_picker' && (
                <ContactPicker
                  value={formData.contact_id}
                  onChange={(contactId, contact) => {
                    handleChange('contact_id', contactId);
                    handleChange('location_id', null); // Reset location when contact changes
                    if (contact) {
                      handleChange('contact_name', contact.company_name);
                      handleChange('contact_email', contact.email);
                      handleChange('contact_phone', contact.phone || contact.mobile);
                    }
                  }}
                  contactType="customer"
                />
              )}
              
              {/* Location Picker */}
              {field.type === 'location_picker' && (
                <LocationPicker
                  contactId={formData.contact_id}
                  value={formData.location_id}
                  onChange={(locationId, location) => {
                    handleChange('location_id', locationId);
                    if (location) {
                      handleChange('location_name', location.location_name);
                      handleChange('location_address', location.address_line1);
                    }
                  }}
                />
              )}
              
              {/* User Multi-Select */}
              {field.type === 'user_multi_select' && (
                <UserMultiSelect
                  value={formData.assigned_users || []}
                  onChange={(userIds) => handleChange('assigned_users', userIds)}
                  moduleKey={field.moduleKey}
                  allowedUserIds={field.name === 'assigned_users' ? assigneeUserIds : undefined}
                  teamMemberUserIds={field.name === 'assigned_users' ? teamMemberUserIds : undefined}
                />
              )}
            </div>
          ))}
          
          {/* Custom Fields (template-based) */}
          {selectedTemplate && selectedTemplate.field_definitions && selectedTemplate.field_definitions.length > 0 && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {selectedTemplate.icon} {selectedTemplate.template_label || selectedTemplate.template_name} Details
              </h3>
              <div className="space-y-4">
                {selectedTemplate.field_definitions.map((field: TemplateField) => {
                  // Convert string[] options to DynamicFieldInput format
                  const convertedField = {
                    ...field,
                    options: field.options?.map(opt => {
                      // Check if already an object
                      if (typeof opt === 'object' && opt !== null && 'value' in opt) {
                        return opt;
                      }
                      // Convert string to object
                      return {
                        value: typeof opt === 'string' ? opt : String(opt),
                        label: typeof opt === 'string' ? opt : String(opt)
                      };
                    })
                  };
                  return (
                    <DynamicFieldInput
                      key={field.key}
                      field={convertedField}
                      value={formData.custom_fields?.[field.key]}
                      onChange={(value) => handleCustomFieldChange(field.key, value)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Workspace Custom Fields (workspace-level config) */}
          {workspaceCustomFields && workspaceCustomFields.length > 0 && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Workspace Fields
              </h3>
              <div className="space-y-4">
                {workspaceCustomFields.map((field) => {
                  const convertedField = {
                    key: field.key,
                    label: field.label,
                    type: field.type as TemplateField['type'],
                    required: field.required,
                    options: field.options?.map(opt => ({ value: opt, label: opt })),
                  };
                  return (
                    <DynamicFieldInput
                      key={field.key}
                      field={convertedField}
                      value={formData.custom_fields?.[field.key]}
                      onChange={(value) => handleCustomFieldChange(field.key, value)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          </div>

          {/* Actions */}
          <div
            className={`flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 ${useSlideOverLayout ? 'flex-shrink-0 px-6 py-4' : 'pt-4'}`}
            style={isCapacitorApp ? { paddingBottom: `calc(${MOBILE_SAFE_AREA_BOTTOM} + 1rem)` } : undefined}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : existingItem ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
  );

  const modalContent = useSlideOverLayout ? (
    <>
      <div
        className="fixed inset-0 z-[120] bg-black/30 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
        aria-hidden="true"
      />
      {modalPanel}
    </>
  ) : (
    <div className={`fixed inset-0 isolate bg-black/50 flex z-[120] ${isCapacitorApp ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'}`}>
      {modalPanel}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
